import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CgSegmentPills, { SegmentDot } from 'modules/Common/ui/CgSegmentPills';
import { FormActions } from 'modules/Common/form/v2';

// The generic Portal Studio inspector-rail chrome: the tab strip, the always-
// mounted panel host, and the sticky save footer. It is surface-agnostic —
// each surface passes its own `tabs` model and `panels` map, so the operator
// and supplier inspectors share one implementation of the rail while owning
// their own settings.
//
// Reads the ambient react-hook-form context for error dots + the dirty
// indicator, so it must be rendered inside a <Form>/<FormProvider>.
//
// Props:
//   tabs        [{ id, label, icon, fields }] — the surface's tab model
//   panels      { [tabId]: ReactNode }        — the surface's panel components
//   activeTab / onTabChange
//   saving / saved
export default function PortalStudioShell({ tabs, panels, activeTab, onTabChange, saving, saved }) {
  const { formState } = useFormContext();
  const segments = tabs.map((tab) => ({
    id: tab.id,
    label: tab.label,
    // The `indicator` slot (rendered before the label) rather than `icon`, so
    // the glyph can be dimmed — descriptive, not competing with the label.
    indicator: (
      <FontAwesomeIcon
        icon={tab.icon}
        className="text-[11px] opacity-60"
      />
    ),
    // A red dot marks a tab holding a validation error the user can't see.
    dot: tab.fields.some((field) => formState.errors[field]) ? '#ef4444' : undefined,
  }));

  return (
    <>
      <div className="px-3 py-2.5 border-b border-slate-200 bg-slate-50/80">
        <CgSegmentPills
          segments={segments}
          activeSegmentId={activeTab}
          onSegmentChange={onTabChange}
          className="flex items-center justify-center gap-1 rounded-xl border border-slate-200 bg-white p-1"
        />
      </div>

      {/* Inactive panels stay MOUNTED (display:none, an inline style so no
          utility-class ordering can flip it back): unmounting them would drop
          their react-hook-form registration and let a hidden field submit as
          undefined. */}
      {/* `overflow-y-auto` is UNCONDITIONAL, never `overflow-visible
          xl:overflow-y-auto` — that pair computed to `visible` and clipped this
          rail instead of scrolling it (CLAUDE.md "two cascade hazards" #1).
          Harmless below `xl`, where the rail has no bounded height and the page
          scrolls instead. */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="max-w-[560px] xl:max-w-none">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              style={{ display: activeTab === tab.id ? undefined : 'none' }}
            >
              {panels[tab.id]}
            </div>
          ))}
        </div>
      </div>

      <div className="px-4 pb-3 border-t border-slate-200 bg-white">
        <FormActions
          submitLabel="Save changes"
          align="between"
          loading={saving}
          submitted={saved}
          extraActions={
            <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
              {formState.isDirty ? (
                <>
                  <SegmentDot color="#f59e0b" />
                  Unsaved changes
                </>
              ) : (
                'All changes saved'
              )}
            </span>
          }
        />
      </div>
    </>
  );
}
